import React, { useEffect, useState } from "react";
import { ErrorMessage, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { connect } from "react-redux";
import axiosInstance from "../../../axios-instance";
import Loader from "../../../components/Loader/Loader";
import QuickSetupLayout from "../../../components/OnboardingSetup/QuickSetupLayout";
import {
  OnboardingActions,
  OnboardingFieldError,
} from "../../../components/OnboardingSetup/OnboardingSetupLayout";
import {
  isOtherEventType,
  isOtherProfession,
} from "../../../components/OnboardingSetup/onboardingSteps";
import {
  getStepThreeData,
  saveStepThreeData,
} from "../../../actions/admin/quick-setup-actions";
import isEmpty from "../../../validation/is-empty";
import { getAdminBusinessId, getUserLandingPage } from "../../../utils/authHelper";
import { notification } from "../../../utils/utility";

const initialValues = {
  role: "",
  other_role: "",
  event_types: [],
  other_event_type: "",
};

const QuickSetupStepThree = (props) => {
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [profession, setProfession] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);

  useEffect(() => {
    setPageLoading(true);

    Promise.all([
      axiosInstance.get("/profession-types"),
      axiosInstance.get("/event-types"),
    ])
      .then(([professionResponse, eventResponse]) => {
        const professions = professionResponse.data.data.profession_types || [];
        const types = eventResponse.data.data.eventTypes || [];
        setProfession(professions);
        setEventTypes(types);

        props.getStepThreeData({
          onSuccess: function (response) {
            const { data } = response;
            if (!isEmpty(data)) {
              const otherProfession = professions.find(isOtherProfession);
              const otherProfessionId = otherProfession
                ? String(otherProfession.id)
                : "7";
              const adminEventTypes = [];
              let otherEventType = "";

              if (!isEmpty(data.event_types)) {
                String(data.event_types)
                  .split(",")
                  .forEach((eventType) => {
                    if (!isNaN(eventType) && eventType !== "") {
                      adminEventTypes.push(String(eventType));
                    } else if (eventType) {
                      adminEventTypes.push("9");
                      otherEventType = eventType;
                    }
                  });
              }

              setFormValues({
                ...initialValues,
                role: isNaN(data.role) ? otherProfessionId : String(data.role),
                event_types:
                  adminEventTypes.length > 0
                    ? adminEventTypes
                    : initialValues.event_types,
                other_event_type: otherEventType || initialValues.other_event_type,
                other_role: isNaN(data.role)
                  ? data.role
                  : initialValues.other_role,
              });
            }
            setPageLoading(false);
          },
          onError: function () {
            setPageLoading(false);
          },
        });
      })
      .catch(() => {
        setPageLoading(false);
        notification.error("Could not load preferences. Refresh and try again.");
      });
  }, []);

  const hasOtherEventInList = eventTypes.some(isOtherEventType);

  return (
    <>
      <Loader isShowLoader={pageLoading} />
      <Formik
        validationSchema={Yup.object().shape({
          role: Yup.string().required(
            "Select the option that best describes your role."
          ),
          event_types: Yup.array()
            .of(Yup.string())
            .min(1, "Select at least one event type."),
        })}
        initialValues={formValues || initialValues}
        enableReinitialize
        onSubmit={(values) => {
          const selectedProfession = profession.find(
            (item) => String(item.id) === String(values.role)
          );
          const payload = { ...values };

          if (isOtherProfession(selectedProfession)) {
            if (!values.other_role.trim()) {
              notification.error("Please specify your role.");
              return;
            }
            payload.role = values.other_role.trim();
          }

          let eventTypeIds = [...values.event_types];
          const otherSelected = eventTypeIds.some((id) => {
            const match = eventTypes.find(
              (item) => String(item.id) === String(id)
            );
            return String(id) === "9" || isOtherEventType(match);
          });
          if (otherSelected) {
            if (!values.other_event_type.trim()) {
              notification.error("Please specify the event type.");
              return;
            }
            eventTypeIds = eventTypeIds.filter((id) => String(id) !== "9");
            eventTypeIds.push(values.other_event_type.trim());
          }

          payload.business_id = getAdminBusinessId();
          setPageLoading(true);

          props.saveStepThreeData({
            data: {
              ...payload,
              event_types: eventTypeIds.join(","),
            },
            onSuccess: (response) => {
              if (response.success) {
                notification.success("Record saved successfully");
                navigate(getUserLandingPage());
              }
              setPageLoading(false);
            },
            onError: () => {
              notification.error(
                "Could not save preferences. Please try again."
              );
              setPageLoading(false);
            },
          });
        }}
      >
        {(formik) => {
          const { values, handleChange, handleSubmit, handleBlur } = formik;
          const selectedProfession = profession.find(
            (item) => String(item.id) === String(values.role)
          );
          const showOtherRole = isOtherProfession(selectedProfession);
          const showOtherEvent =
            values.event_types.includes("9") ||
            values.event_types.some((id) => {
              const match = eventTypes.find(
                (item) => String(item.id) === String(id)
              );
              return isOtherEventType(match);
            });

          return (
            <QuickSetupLayout
              currentStep={3}
              title="How you use MeetOcto"
              description="This only helps us tailor examples — it does not limit what you can create."
            >
              <form
                className="onboarding-setup__form"
                onSubmit={handleSubmit}
                noValidate
              >
                <div className="onboarding-setup__panels">
                  <section className="onboarding-setup__panel">
                    <h2 className="onboarding-setup__panel-title">Your role</h2>
                    <div
                      className="onboarding-setup__choices"
                      role="radiogroup"
                      aria-labelledby="role_label"
                    >
                      <span className="visually-hidden" id="role_label">
                        Your role
                      </span>
                      {profession.map((item) => (
                        <label className="onboarding-setup__choice" key={item.id}>
                          <input
                            type="radio"
                            name="role"
                            value={item.id}
                            checked={String(values.role) === String(item.id)}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <span>{item.profession}</span>
                        </label>
                      ))}
                    </div>
                    <ErrorMessage name="role" component={OnboardingFieldError} />
                    {showOtherRole ? (
                      <div className="onboarding-setup__field">
                        <label className="onboarding-setup__label" htmlFor="other_role">
                          Specify your role
                        </label>
                        <input
                          type="text"
                          className="onboarding-setup__input"
                          name="other_role"
                          id="other_role"
                          placeholder="Your role"
                          value={values.other_role}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </div>
                    ) : null}
                  </section>

                  <section className="onboarding-setup__panel">
                    <h2 className="onboarding-setup__panel-title">Event types</h2>
                    <div
                      className="onboarding-setup__choices"
                      role="group"
                      aria-labelledby="events_label"
                    >
                      <span className="visually-hidden" id="events_label">
                        Main event types
                      </span>
                      {eventTypes.map((item) => (
                        <label className="onboarding-setup__choice" key={item.id}>
                          <input
                            type="checkbox"
                            name="event_types"
                            value={item.id}
                            checked={values.event_types.includes(String(item.id))}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <span>{item.event_type}</span>
                        </label>
                      ))}
                      {!hasOtherEventInList ? (
                        <label className="onboarding-setup__choice">
                          <input
                            type="checkbox"
                            name="event_types"
                            value="9"
                            checked={values.event_types.includes("9")}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <span>Other</span>
                        </label>
                      ) : null}
                    </div>
                    <ErrorMessage name="event_types" component={OnboardingFieldError} />
                    {showOtherEvent ? (
                      <div className="onboarding-setup__field">
                        <label
                          className="onboarding-setup__label"
                          htmlFor="other_event_type"
                        >
                          Specify event type
                        </label>
                        <input
                          type="text"
                          className="onboarding-setup__input"
                          name="other_event_type"
                          id="other_event_type"
                          placeholder="Event type"
                          value={values.other_event_type}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </div>
                    ) : null}
                  </section>
                </div>

                <OnboardingActions
                  showSkip={false}
                  onBack={() => navigate("/admin/quick-setup-step-two")}
                  submitLabel="Finish"
                />
              </form>
            </QuickSetupLayout>
          );
        }}
      </Formik>
    </>
  );
};

export default connect(null, {
  getStepThreeData,
  saveStepThreeData,
})(QuickSetupStepThree);
